<script setup lang="ts">
import type { FieldGroupSection, Theme } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';
import { computed } from 'vue';

import FieldRenderer from './FieldRenderer.vue';

defineProps<{
  section: FieldGroupSection;
}>();

const reportStore = useUpdatedReportStore();

const theme = computed<Theme | null>(() => {
  const loadedTemplate = reportStore.getTemplate();
  return loadedTemplate?.theme ?? null;
});

const titleStyle = computed(() => {
  if (!theme.value) return {};
  const sl = theme.value.fonts.sectionLabel;
  return {
    fontFamily: sl.family,
    fontSize: `${sl.size}px`,
    fontWeight: sl.weight,
    color: theme.value.colors.muted,
    textTransform: sl.uppercase ? 'uppercase' : 'none',
    letterSpacing: sl.letterSpacing ? `${sl.letterSpacing}px` : undefined,
  };
});

const borderStyle = computed(() => {
  if (!theme.value) return {};
  return {
    backgroundColor: theme.value.colors.border,
  };
});

const rowsGapStyle = computed(() => {
  if (!theme.value) return {};
  return {
    gap: `${
      theme.value.spacing.fieldGap + theme.value.spacing.sectionGap * 0.5 - 3
    }px`,
  };
});

const fieldGapStyle = computed(() => {
  if (!theme.value) return {};
  return {
    gap: `${theme.value.spacing.fieldGap}px`,
  };
});
</script>

<template>
  <div class="field-group-section">
    <!-- Section title -->
    <div v-if="section.title" class="section-title-block">
      <span class="section-title" :style="titleStyle">
        {{ section.title }}
      </span>
      <div class="section-title-border" :style="borderStyle" />
    </div>

    <!-- Field rows -->
    <div class="section-rows" :style="rowsGapStyle">
      <div
        v-for="(row, rowIdx) in section.rows"
        :key="rowIdx"
        class="field-row"
        :style="fieldGapStyle"
      >
        <div
          v-for="field in row"
          :key="field.key"
          class="field-cell"
          :style="{ flex: field.width ?? 1 }"
        >
          <FieldRenderer :field="field" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.field-group-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.section-title-block {
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 11px;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
}

.section-title-border {
  width: 100%;
  height: 1px;
  background-color: #a3a3a3;
  margin-top: 2px;
}

.section-rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-row {
  display: flex;
  gap: 8px;
}

.field-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
